import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import strings from "../global/strings";
import { omitEmpty } from "./query-utils";

const LIMIT = 10;

export const customersQueryKey = ["customers"] as const;

// Mirrors the company_customers table (billing_system_BE/models/companyCustomer.js)
// — this is the row shape returned by customer/company, customer/search, and
// customer/update (not customer_info, the separate cross-company dedup table).
export interface Customer {
	company_customer_id: string;
	customer_info_id?: string | null;
	company_info_id?: string;
	customer_id: string;
	customer_name: string;
	customer_tag?: string | null;
	customer_gender?: string | null;
	customer_type: string;
	customer_pan_vat?: string | null;
	customer_image?: string | null;
	customer_address?: string | null;
	customer_city?: string | null;
	customer_email?: string | null;
	customer_mobile: string;
	createdAt?: string;
	updatedAt?: string;
	// FE-only flag, set client-side after a successful add (see customer-profile.component.tsx)
	isNew?: boolean;
}

export interface CustomerFilters {
	customer_id?: string;
	customer_pan_vat?: string;
	customer_name?: string;
	customer_email?: string;
	customer_mobile?: string;
	customer_type?: string;
	customer_gender?: string;
	customer_tag?: string;
}

interface CustomersPageResult {
	rows: Customer[];
	count: number;
}

// Mirrors fetchCompanyCustomers/searchCustomers in redux/actions/customer.action.js:
// no filters -> customer/company, any filter present -> customer/search. Filters
// are stripped of empty values here (not just at the caller) so a filters object
// whose keys are all "" still correctly counts as no filters applied.
async function fetchCustomersPage(
	filters: CustomerFilters,
	page: number,
): Promise<CustomersPageResult> {
	const cleanFilters = omitEmpty(filters);
	const hasFilters = Object.keys(cleanFilters).length > 0;

	const url = `${strings.url}customer/${hasFilters ? "search" : "company"}`;
	const params = { ...cleanFilters, limit: LIMIT, page };

	const res = await axios.get(url, { params, withCredentials: true });

	if (!res.data.success) {
		throw new Error(res.data.message);
	}

	return { rows: res.data.result.rows, count: res.data.result.count };
}

// Infinite-scroll customer list.
export function useCustomers(filters: CustomerFilters) {
	return useInfiniteQuery({
		queryKey: [...customersQueryKey, filters],
		queryFn: ({ pageParam }) => fetchCustomersPage(filters, pageParam),
		initialPageParam: 1,
		getNextPageParam: (lastPage, allPages) => {
			const fetchedCount = allPages.reduce((sum, p) => sum + p.rows.length, 0);
			return fetchedCount < lastPage.count ? allPages.length + 1 : undefined;
		},
	});
}

// One-shot (non-paginated) fetch of every customer matching filters. Used by
// customer-bulk-draft's "select clients to draft" list (needs the full match
// set rendered at once, not an infinite-scroll page) and by useCustomerSearch
// (the invoice-form buyer autocomplete, fed a 2-char-prefix filter instead —
// `enabled` lets it stay idle until the user has actually typed something).
// Mirrors the old getAllFilteredClients/searchCustomers thunks: always hits
// customer/search with no limit/page, even with empty filters (the BE treats
// an empty where-clause as "all company customers"), unlike
// fetchCustomersPage's hasFilters branch which chooses company vs. search and
// paginates. Filters come from ad hoc filter-form shapes (a superset of
// CustomerFilters, e.g. total_sales_min/max), so this deliberately takes a
// loose bag rather than the strict type.
export function useFilteredCustomers(
	filters: Record<string, unknown>,
	enabled = true,
) {
	return useQuery({
		queryKey: [...customersQueryKey, "filtered", filters],
		queryFn: async () => {
			const cleanFilters = omitEmpty(filters);
			const res = await axios.get(`${strings.url}customer/search`, {
				params: cleanFilters,
				withCredentials: true,
			});

			if (!res.data.success) {
				throw new Error(res.data.message);
			}

			return res.data.result.rows as Customer[];
		},
		enabled,
	});
}

// Everything sent in the add/update request body is a subset of Customer's
// own fields (the formik form is seeded from this same shape) — no separate
// payload type needed.
type CustomerPayload = Partial<Customer>;

interface CustomerDuplication {
	email?: boolean;
	mobile?: boolean;
	pan_vat?: boolean;
}

interface AddCustomerResult {
	success: boolean;
	result?: Customer;
	message?: string;
	duplication?: CustomerDuplication;
}

// Duplication is a structured validation response the caller renders as
// per-field formik errors, not a mutation failure — only throw for real
// failures so `mutation.isError` (FormError) is reserved for those.
async function postAddCustomer(customerData: CustomerPayload): Promise<AddCustomerResult> {
	const res = await axios.post(`${strings.url}customer/add`, customerData, {
		withCredentials: true,
	});

	if (!res.data.success && !res.data.duplication) {
		throw new Error(res.data.message);
	}

	return res.data;
}

export function useAddCustomer() {
	return useMutation({
		mutationFn: postAddCustomer,
	});
}

interface UpdateCustomerVars {
	customerData: CustomerPayload;
	page?: number;
}

// Mirrors AddCustomerResult's shape — duplication is a structured validation
// response the caller renders as per-field formik errors, not a mutation
// failure (see putUpdateCustomer below for why this needs its own handling,
// unlike the add path's same-shaped response).
interface UpdateCustomerResult {
	success: boolean;
	rows?: Customer[];
	message?: string;
	duplication?: CustomerDuplication;
}

// Mirrors updateCustomer in redux/actions/customer.action.js: the update
// endpoint returns a full page of rows (not just the updated row), used to
// keep customerReducer.customersList in sync for consumers that still read
// it (invoice-form/customer-bulk-draft autocomplete).
async function putUpdateCustomer({
	customerData,
	page = 1,
}: UpdateCustomerVars): Promise<UpdateCustomerResult> {
	try {
		const res = await axios.put(`${strings.url}customer/update`, customerData, {
			params: { limit: LIMIT, page },
			withCredentials: true,
		});

		if (!res.data.success) {
			throw new Error(res.data.message);
		}

		return { success: true, rows: res.data.result.rows };
	} catch (err) {
		// Unlike customer/add (which returns its duplication response as a
		// plain 200), customer/update's duplicate-record check
		// (helper_duplicateCompanyCustomer) responds with HTTP 400, so axios
		// throws — the structured duplication payload lives on
		// err.response.data, not a normal resolved response. Still a soft,
		// field-level validation result, not a real mutation failure, so it's
		// returned rather than re-thrown; genuine failures fall through below.
		if (axios.isAxiosError(err) && err.response?.data?.duplication) {
			return {
				success: false,
				message: err.response.data.message,
				duplication: err.response.data.duplication,
			};
		}
		throw err;
	}
}

export function useUpdateCustomer() {
	return useMutation({
		mutationFn: putUpdateCustomer,
	});
}

interface CustomerBill {
	invoice_number?: string;
	invoice_date?: string;
	sub_total?: number;
	bill_void?: boolean;
}

// customer/id's response is a full Customer row (CompanyCustomers.findOne) plus
// the included Bill association (billing_system_BE/controllers/customer.controller.js
// getCustomerById) — not a distinct shape.
export interface CustomerDetails extends Customer {
	bills?: CustomerBill[];
}

async function fetchCustomerDetails(
	companyCustomerId: string,
	limit: number,
): Promise<CustomerDetails> {
	const res = await axios.get(`${strings.url}customer/id`, {
		params: { company_customer_id: companyCustomerId, limit },
		withCredentials: true,
	});

	if (!res.data.success) {
		throw new Error(res.data.message);
	}

	return res.data.result;
}

// Self-contained to the customer-profile side-modal (bill preview list) — no
// other page/component reads this data, unlike customersQueryKey above which
// several pages share.
export function useCustomerDetails(companyCustomerId?: string, limit = 5) {
	return useQuery({
		queryKey: ["customerDetails", companyCustomerId, limit],
		queryFn: () => fetchCustomerDetails(companyCustomerId as string, limit),
		enabled: Boolean(companyCustomerId),
	});
}
